import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

function formatDisplayPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

const contactDef: SlotGroupDefinition = {
  label: 'Leasing Contact',
  fields: {
    name: {
      label: 'Contact Name',
      type: 'text',
      defaultValue: 'Marcelo Grosberg',
    },
    company: {
      label: 'Company',
      type: 'text',
      defaultValue: 'Segall Group',
    },
    email: {
      label: 'Email',
      type: 'email',
      defaultValue: 'mgrosberg@segallgroup.com',
    },
    phone: {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_phone',
      defaultValue: '(410) 970-3766',
    },
  },
};

/**
 * Retail leasing contact band — mirrors the "FOR RETAIL LEASING, CONTACT"
 * block on the Odenton Station retail page.
 */
export function RetailLeasingContact() {
  const contact = useSlotGroup('retailleasingcontact.contact', contactDef);
  const phoneDisplay = formatDisplayPhone(contact.phone) || contact.phone || '';
  const phoneDigits = contact.phone?.replace(/\D/g, '') || '';
  const phoneHref = phoneDigits ? `tel:${phoneDigits}` : '';

  useRevealStyles();
  const ref = useScrollReveal<HTMLDivElement>();

  return (
    <section className="retailleasing bg-primary text-primary-foreground py-14 md:py-20 px-6">
      <div
        ref={ref}
        className="reveal-fade-up mx-auto max-w-2xl text-center flex flex-col items-center gap-3"
      >
        <RichTextContent
          path="retailleasingcontact.heading"
          label="Heading"
          defaultValue="<p>FOR RETAIL LEASING, CONTACT:</p>"
          as="h2"
          className="font-body text-sm sm:text-base font-medium tracking-[0.22em] uppercase text-primary-foreground mb-2"
        />

        <EditableSlotGroup
          groupPath="retailleasingcontact.contact"
          definition={contactDef}
        >
          <div className="flex flex-col items-center gap-1.5 font-body">
            {contact.name?.trim() ? (
              <p className="text-lg sm:text-xl font-heading font-bold tracking-wide uppercase m-0">
                {contact.name.trim()}
              </p>
            ) : null}
            {contact.company?.trim() ? (
              <p className="text-base opacity-90 m-0">{contact.company.trim()}</p>
            ) : null}
            {contact.email?.trim() ? (
              <a
                href={`mailto:${contact.email.trim()}`}
                className="text-base text-primary-foreground underline underline-offset-4 decoration-primary-foreground/50 hover:decoration-primary-foreground m-0"
              >
                {contact.email.trim()}
              </a>
            ) : null}
            {phoneDisplay ? (
              phoneHref ? (
                <a
                  href={phoneHref}
                  className="text-base text-primary-foreground underline underline-offset-4 decoration-primary-foreground/50 hover:decoration-primary-foreground m-0"
                >
                  {phoneDisplay}
                </a>
              ) : (
                <p className="text-base m-0">{phoneDisplay}</p>
              )
            ) : null}
          </div>
        </EditableSlotGroup>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'RetailLeasingContact',
  displayName: 'Retail Leasing Contact',
  tokens: {
    'retailleasingcontact.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>FOR RETAIL LEASING, CONTACT:</p>',
    },
  },
  slotGroups: {
    'retailleasingcontact.contact': contactDef,
  },
};

(RetailLeasingContact as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default RetailLeasingContact;
